import React from 'react';
import { MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';

import { Spinner } from '@oysterjs/ui/spinner';

import { usePagedDocumentsEnabled } from './use-paged-documents-enabled';

/** A box on a page, as fractions of that page from its top-left corner. */
export interface PageRegion {
  page: number;
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface MarkedRegion extends PageRegion {
  id: string;
  active?: boolean;
}

/** Four decimals of a page is a tenth of a point; enough, and stable to compare. */
const round4 = (n: number) => Math.round(n * 10000) / 10000;

/** Smaller than this and a drag was a click, not a box. */
const MIN_BOX_PX = 8;

/** A Letter page at CSS resolution; zoom is a multiple of this width. */
export const PAGE_WIDTH_PX = 8.5 * 96;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2;
const ZOOM_STEP = 1.25;
/** The frame's own padding either side of the pages. */
const FRAME_PADDING_PX = 32;

/**
 * The zoom at which one page spans the frame. A frame that has not been measured yet, or one
 * jsdom cannot measure, fits at the page's natural size rather than at the floor.
 */
export const fitWidthZoom = (frameWidthPx: number): number =>
  frameWidthPx > 0
    ? Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, (frameWidthPx - FRAME_PADDING_PX) / PAGE_WIDTH_PX))
    : 1;

const clampZoom = (zoom: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

/**
 * Zoom for the pages. Opens at fit width so a narrow panel shows whole pages, and never wider
 * than the page's own size, which is already as large as the type was set. Below about half
 * size the pages wrap into columns, which is how several pages come to sit side by side.
 */
const usePagesZoom = (frameRef: React.RefObject<HTMLDivElement | null>, mounted: boolean) => {
  const [frameWidth, setFrameWidth] = React.useState(0);
  const [zoom, setZoom] = React.useState<number | null>(null);

  // The frame only exists once the pages do, so the measurement waits for it.
  React.useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!mounted || !frame) return;
    const measure = () => setFrameWidth(frame.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [frameRef, mounted]);

  const fitted = fitWidthZoom(frameWidth);
  const current = zoom ?? Math.min(1, fitted);
  return {
    zoom: current,
    step: (by: number) => setZoom(clampZoom(current * by)),
    fit: () => setZoom(fitted)
  };
};

/**
 * Shows a PDF as its pages and nothing else. The browser's own PDF viewer paints a toolbar
 * and a thumbnail rail that cannot be turned off from outside, so statics renders the pages
 * to images (`?pages=1` on any PDF route) and this draws them in a plain scroll. The PDF
 * itself stays one click away for anyone who wants to zoom, search, or save.
 */
export const PdfPagesViewer = (props: {
  src: string;
  title: string;
  className?: string;
  /** Fires once the pages are drawn, or once drawing them has failed. */
  onLoad?: () => void;
  /** Boxes to draw over the pages, one per comment. */
  regions?: MarkedRegion[];
  /** When set, dragging on a page draws a box and hands it back. */
  onDrawRegion?: (region: PageRegion) => void;
  onRegionClick?: (id: string) => void;
}) => {
  const pagedDocuments = usePagedDocumentsEnabled();
  const [drawing, setDrawing] = React.useState<{
    page: number;
    startX: number;
    startY: number;
    x: number;
    y: number;
  } | null>(null);
  const [state, setState] = React.useState<
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'ready'; pages: string[]; totalPages: number }
  >({ status: 'loading' });
  const [attempt, setAttempt] = React.useState(0);
  // Pages arrive one by one behind the list, so each is covered until its image has settled.
  const [settled, setSettled] = React.useState<Set<number>>(new Set());
  const [failed, setFailed] = React.useState<Set<number>>(new Set());
  const settle = (page: number) =>
    setSettled((prev) => (prev.has(page) ? prev : new Set(prev).add(page)));

  const onLoadRef = React.useRef(props.onLoad);
  onLoadRef.current = props.onLoad;

  React.useEffect(() => {
    let canceled = false;
    const controller = new AbortController();
    setState({ status: 'loading' });
    setSettled(new Set());
    setFailed(new Set());
    setDrawing(null);
    fetch(
      pagesUrl(props.src, window.devicePixelRatio),
      pagedDocuments
        ? { credentials: 'include', signal: controller.signal }
        : { credentials: 'include' }
    )
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((body: { pages?: { url: string }[]; totalPages?: number }) => {
        if (canceled) return;
        const pages = (body.pages ?? []).map((page) => absolute(page.url, props.src));
        setState(
          pages.length > 0
            ? { status: 'ready', pages, totalPages: body.totalPages ?? pages.length }
            : { status: 'error' }
        );
        onLoadRef.current?.();
      })
      .catch(() => {
        if (canceled) return;
        setState({ status: 'error' });
        onLoadRef.current?.();
      });
    return () => {
      canceled = true;
      controller.abort();
    };
  }, [props.src, attempt, pagedDocuments]);

  const activeRegionRef = React.useRef<HTMLButtonElement | null>(null);
  const activeRegion = props.regions?.find((region) => region.active);
  const activeRegionReady = !!activeRegion && settled.has(activeRegion.page);
  React.useEffect(() => {
    if (activeRegionReady) {
      activeRegionRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [activeRegion?.id, activeRegionReady]);

  const frameRef = React.useRef<HTMLDivElement | null>(null);
  const { zoom, step, fit } = usePagesZoom(frameRef, state.status === 'ready');

  const frame = `flex-1 min-h-0 w-full overflow-auto rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-100 dark:bg-neutral-700 ${props.className ?? ''}`;

  if (state.status === 'loading') {
    return (
      <div
        className={`${frame} flex flex-col items-center justify-center gap-3 text-sm text-neutral-600 dark:text-neutral-400`}
        data-testid="pdf-pages-loading"
      >
        <Spinner size="size-8" color="neutral" />
        <span>Rendering the document…</span>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div
        className={`${frame} flex flex-col items-center justify-center gap-2 text-sm text-neutral-600 dark:text-neutral-400`}
      >
        <span>The preview could not be drawn.</span>
        <button
          type="button"
          onClick={() => setAttempt((value) => value + 1)}
          className="text-primary-600 dark:text-primary-400 hover:underline"
        >
          Retry preview
        </button>
        <a
          href={props.src}
          target="_blank"
          rel="noreferrer"
          className="text-primary-600 dark:text-primary-400 hover:underline"
        >
          Open the PDF
        </a>
      </div>
    );
  }
  const fractions = (e: React.MouseEvent<HTMLElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - box.left) / box.width)),
      y: Math.min(1, Math.max(0, (e.clientY - box.top) / box.height)),
      widthPx: box.width,
      heightPx: box.height
    };
  };

  const finishDrawing = (e: React.MouseEvent<HTMLElement>) => {
    if (!drawing || !props.onDrawRegion) return;
    const { x, y, widthPx, heightPx } = fractions(e);
    const left = Math.min(drawing.startX, x);
    const top = Math.min(drawing.startY, y);
    const width = Math.abs(x - drawing.startX);
    const height = Math.abs(y - drawing.startY);
    setDrawing(null);
    if (width * widthPx < MIN_BOX_PX || height * heightPx < MIN_BOX_PX) return;
    props.onDrawRegion({
      page: drawing.page,
      left: round4(left),
      top: round4(top),
      // Rounding both origin and size can otherwise push an edge box beyond the page.
      width: Math.min(round4(width), 1 - round4(left)),
      height: Math.min(round4(height), 1 - round4(top))
    });
  };

  const canDraw = !!props.onDrawRegion;
  const completed = state.pages.filter((_, i) => settled.has(i + 1) || failed.has(i + 1)).length;
  const nextPage = state.pages.findIndex((_, i) => !settled.has(i + 1) && !failed.has(i + 1)) + 1;

  return (
    <div ref={frameRef} className={`${frame} p-4`} role="document" aria-label={props.title}>
      {state.totalPages > state.pages.length && (
        <div
          role="note"
          className="mb-3 rounded-md bg-white dark:bg-neutral-800 p-2 text-sm text-neutral-600 dark:text-neutral-400"
        >
          Showing the first {state.pages.length} of {state.totalPages} pages.{' '}
          <a
            href={props.src}
            target="_blank"
            rel="noreferrer"
            className="text-primary-600 dark:text-primary-400 hover:underline"
          >
            Open the full PDF
          </a>
        </div>
      )}
      <div className="pointer-events-none sticky top-0 z-10 flex items-center justify-between gap-2">
        {completed < state.pages.length ? (
          <div
            role="status"
            className="flex items-center gap-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white/90 dark:bg-neutral-900/90 px-2 py-1 text-xs text-neutral-600 dark:text-neutral-400 shadow-sm backdrop-blur"
          >
            <Spinner size="size-3.5" color="neutral" />
            Rendering page {nextPage} of {state.pages.length}
          </div>
        ) : (
          <span />
        )}
        <div className="pointer-events-auto flex items-center gap-0.5 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white/90 dark:bg-neutral-900/90 p-0.5 shadow-sm backdrop-blur">
          <ZoomAction
            label="Zoom out"
            disabled={zoom <= MIN_ZOOM + 0.001}
            onClick={() => step(1 / ZOOM_STEP)}
          >
            <MagnifyingGlassMinusIcon className="size-4" />
          </ZoomAction>
          <button
            type="button"
            onClick={fit}
            title="Fit width"
            data-testid="pdf-pages-zoom"
            className="min-w-12 rounded-md px-1 py-1 text-xs font-medium tabular-nums text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-white/10"
          >
            {Math.round(zoom * 100)}%
          </button>
          <ZoomAction
            label="Zoom in"
            disabled={zoom >= MAX_ZOOM - 0.001}
            onClick={() => step(ZOOM_STEP)}
          >
            <MagnifyingGlassPlusIcon className="size-4" />
          </ZoomAction>
        </div>
      </div>
      <div className="-mt-6 flex flex-wrap justify-center gap-4">
        {state.pages.map((src, i) => {
          const page = i + 1;
          const live =
            drawing && drawing.page === page
              ? {
                  left: Math.min(drawing.startX, drawing.x),
                  top: Math.min(drawing.startY, drawing.y),
                  width: Math.abs(drawing.x - drawing.startX),
                  height: Math.abs(drawing.y - drawing.startY)
                }
              : null;
          return (
            <div
              key={page}
              className={
                pagedDocuments
                  ? `relative shrink-0 select-none ${canDraw ? 'cursor-crosshair' : ''}`
                  : 'relative shrink-0'
              }
              style={{ width: `${PAGE_WIDTH_PX * zoom}px`, maxWidth: '100%' }}
              data-testid={`pdf-page-${page}`}
              onMouseDown={(e) => {
                if (!canDraw || e.button !== 0) return;
                e.preventDefault();
                const { x, y } = fractions(e);
                setDrawing({ page, startX: x, startY: y, x, y });
              }}
              onMouseMove={(e) => {
                if (!drawing || drawing.page !== page) return;
                const { x, y } = fractions(e);
                setDrawing({ ...drawing, x, y });
              }}
              onMouseUp={finishDrawing}
              onMouseLeave={(e) => {
                if (drawing && drawing.page === page) finishDrawing(e);
              }}
            >
              <img
                src={src}
                alt={`${props.title}, page ${page}`}
                className="block w-full bg-white dark:bg-neutral-800 shadow-md"
                style={{ aspectRatio: 'auto 8.5 / 11' }}
                draggable={false}
                onLoad={() => settle(page)}
                onError={() =>
                  setFailed((prev) => (prev.has(page) ? prev : new Set(prev).add(page)))
                }
              />
              {!settled.has(page) && (
                <div
                  data-testid={`pdf-page-placeholder-${page}`}
                  className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white dark:bg-neutral-800 text-xs text-neutral-400 dark:text-neutral-500 shadow-md"
                  role={failed.has(page) ? 'alert' : undefined}
                >
                  {failed.has(page) ? (
                    <div className="pointer-events-auto flex flex-col items-center gap-1">
                      <span>Page {page} could not be drawn.</span>
                      <button
                        type="button"
                        onClick={() => setAttempt((value) => value + 1)}
                        className="text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        Retry preview
                      </button>
                    </div>
                  ) : (
                    `Page ${page}`
                  )}
                </div>
              )}
              {(props.regions ?? [])
                .filter((r) => r.page === page)
                .map((r) => (
                  <button
                    key={r.id}
                    ref={r.active ? activeRegionRef : undefined}
                    type="button"
                    aria-label="Comment on this area"
                    data-testid={`comment-region-${r.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      props.onRegionClick?.(r.id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className={`absolute rounded-sm border-2 ${
                      r.active
                        ? 'border-amber-500 bg-amber-300/30 dark:bg-amber-500/30'
                        : 'border-amber-400 bg-amber-200/20 dark:bg-amber-500/25 hover:bg-amber-300/30 dark:hover:bg-amber-500/30'
                    }`}
                    style={{
                      left: `${r.left * 100}%`,
                      top: `${r.top * 100}%`,
                      width: `${r.width * 100}%`,
                      height: `${r.height * 100}%`
                    }}
                  />
                ))}
              {live && (
                <div
                  data-testid="drawing-region"
                  className="absolute rounded-sm border-2 border-dashed border-primary-500 bg-primary-300/20 dark:bg-primary-500/30 pointer-events-none"
                  style={{
                    left: `${live.left * 100}%`,
                    top: `${live.top * 100}%`,
                    width: `${live.width * 100}%`,
                    height: `${live.height * 100}%`
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ZoomAction = (props: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-label={props.label}
    title={props.label}
    disabled={props.disabled}
    onClick={props.onClick}
    className="shrink-0 rounded-md p-1 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
  >
    {props.children}
  </button>
);

/** A page URL comes back relative to the statics host that served the list. */
const absolute = (url: string, from: string): string => {
  try {
    return new URL(url, from).toString();
  } catch {
    return url;
  }
};

/**
 * The pages route is the PDF route plus a flag, so one URL serves both. The density asks for
 * enough pixels to stay sharp on this screen; the server caps it.
 */
export const pagesUrl = (pdfUrl: string, devicePixelRatio = 1): string => {
  const [base, fragment] = pdfUrl.split('#', 2);
  const dpi = Math.round(110 * Math.min(2, Math.max(1, devicePixelRatio)));
  const joined = `${base}${base.includes('?') ? '&' : '?'}pages=1&dpi=${dpi}`;
  return fragment ? `${joined}#${fragment}` : joined;
};
